import React, { ReactNode, useState, useContext, useRef, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "./logo_color.png";
import IconExpand from "@/icons/IconExpand";
import IconCollapse from "@/icons/IconCollapse";
import useMenuItems from "@/hooks/useMenuItems";
import { MenuItem } from "@/types";
import MainLink from "./MainLink";
import ProfileButton from "./ProfileButton";
import { ProfileContext } from "@velaro/velaro-component-lib";
import { StatusContext } from "@velaro/velaro-component-lib";
import ExpandableMenuLink from "./ExpandableMenuLink";
const LogoWhite = "/img/Logo_White.svg";
const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;
import { LinkExternal01 } from "@velaro/velaro-component-lib";
import DesktopProfileMenu from "./DesktopProfileMenu";
import Tooltip from "@/components/Tooltip";
import CopilotAssistant from "@/components/CopilotAssistant";
import useVersion from "@/hooks/useVersion";

interface Props {
  children: ReactNode;
}

const styles = {
  subLink:
    "ml-5 p-2 v-nav-hover block rounded-lg transition duration-200 delay-100 hover:delay-100",
  subLinkActive:
    "ml-5 p-2 rounded-lg v-nav-active block transition duration-200 delay-100 hover:delay-100"
};

function SubLink({
  to,
  label,
  onVisit
}: {
  to: string;
  label: string;
  onVisit(): void;
}) {
  if (to.startsWith("http")) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className={styles.subLink}
        onClick={onVisit}
      >
        <span className="w-full text-left text-sm">{label}</span>
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        isActive ? styles.subLinkActive : styles.subLink
      }
      onClick={onVisit}
    >
      <span className="w-full text-left text-sm">{label}</span>
    </NavLink>
  );
}
export default function MainLayout(props: Props) {
  const [leftNavExpanded, setLeftNavExpanded] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [toggleSidebar, setToggleSidebar] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);

  // Content font size — persisted in localStorage. Range 80–130, step 5.
  // Default is 115% — set above 100 for better readability out of the box.
  const [contentFontPct, setContentFontPct] = useState<number>(() => {
    try { return parseInt(localStorage.getItem("v-content-font-pct") || "115", 10) || 115; }
    catch { return 115; }
  });
  const changeFontSize = (delta: number) => {
    setContentFontPct((prev) => {
      const next = Math.max(80, Math.min(130, prev + delta));
      try { localStorage.setItem("v-content-font-pct", String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Draggable FAB — position persisted in sessionStorage
  const [fabPos, setFabPos] = useState<{ bottom: number; right: number }>(() => {
    try {
      const saved = sessionStorage.getItem("copilot-fab-pos");
      return saved ? JSON.parse(saved) : { bottom: 24, right: 24 };
    } catch { return { bottom: 24, right: 24 }; }
  });
  const fabDragStart = useRef<{ mx: number; my: number; ob: number; or: number } | null>(null);
  const fabDragging = useRef(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!fabDragStart.current) return;
      // Require at least 5px of movement before treating as a drag — prevents
      // tiny mouse jitter from swallowing the click event
      const dx = e.clientX - fabDragStart.current.mx;
      const dy = e.clientY - fabDragStart.current.my;
      if (!fabDragging.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      fabDragging.current = true;
      const newBottom = fabDragStart.current.ob - (e.clientY - fabDragStart.current.my);
      const newRight  = fabDragStart.current.or  - (e.clientX - fabDragStart.current.mx);
      const next = {
        bottom: Math.max(8, Math.min(newBottom, window.innerHeight - 56)),
        right:  Math.max(8, Math.min(newRight,  window.innerWidth  - 56)),
      };
      setFabPos(next);
      try { sessionStorage.setItem("copilot-fab-pos", JSON.stringify(next)); } catch { /* ignore */ }
    };
    const onUp = () => { fabDragStart.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = useMenuItems();

  // Map integration URL segments to their IntegrationLogger key names
  const INTEGRATION_KEY_MAP: Record<string, string> = {
    netsuite: "NetSuite",
    hubspot: "HubSpot",
    dynamics: "Dynamics",
    shopify: "Shopify",
    bigcommerce: "BigCommerce",
    woocommerce: "WooCommerce",
    magento: "Magento",
    square: "Square",
    salesforce: "Salesforce",
    accela: "AccelaCivic",
  };
  const integrationKeyMatch = location.pathname.match(/\/integrations\/([^/]+)/);
  const integrationKey = integrationKeyMatch
    ? INTEGRATION_KEY_MAP[integrationKeyMatch[1].toLowerCase()]
    : undefined;

  const { profile } = useContext(ProfileContext);
  const { status } = useContext(StatusContext);
  const { showReloadModal, resetModal } = useVersion();

  const availabilityColor =
    status === "Available"
      ? "bg-green-500"
      : status === "Unavailable"
      ? "bg-sweet-orange-500"
      : "bg-slate-500";

  const sidebarClass = toggleSidebar ? "w-64" : "w-14";
  const toggleSidebarIcon = () => setToggleSidebar((prev) => !prev);

  const expandMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const index = prev.indexOf(menuId);
      return index > -1
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId];
    });
  };

  const renderMenu = (item: MenuItem, index: number) => {
    if (!item.enabled) return null;

    const onVisit = () => {
      if (!leftNavExpanded) setLeftNavExpanded(false);
    };

    if (!item.sublinks) {
      return (
        <MainLink
          key={index}
          label={item.label}
          to={item.to}
          icon={item.icon}
          onVisit={onVisit}
          showTooltip={toggleSidebar}
          toggleSidebar={toggleSidebar}
        />
      );
    }

    return (
      <ExpandableMenuLink
        key={index}
        onExpand={() => expandMenu(item.label)}
        label={toggleSidebar ? item.label : ""}
        icon={item.icon}
        expanded={expandedMenus.includes(item.label)}
        toggleSidebar={toggleSidebar}
      >
        {leftNavExpanded &&
          item.sublinks.map((sublink) =>
            sublink.enabled ? (
              !toggleSidebar ? (
                <SubLink
                  key={sublink.to}
                  to={sublink.to}
                  label={sublink.label}
                  onVisit={onVisit}
                />
              ) : (
                <div key={sublink.to} className="border-l ml-5">
                  <SubLink
                    to={sublink.to}
                    label={sublink.label}
                    onVisit={onVisit}
                  />
                </div>
              )
            ) : null
          )}
      </ExpandableMenuLink>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="main-layout flex h-screen overflow-hidden">
        <div
          className={`relative flex flex-col flex-none px-2 v-nav z-50 ${
            toggleSidebar ? "w-64" : "w-[52px]"
          } ${leftNavExpanded ? "menu_active" : ""}`}
        >
          <div className="flex items-center justify-between pt-6 pb-5">
            <img
              src={LogoWhite}
              alt="Logo"
              className="flex-shrink-0 cursor-pointer"
              style={{ width: 44, height: 38, minWidth: 44, minHeight: 38 }}
              onClick={() => navigate("/")}
            />
            <Tooltip
              text={toggleSidebar ? "Collapse" : "Expand"}
              direction="right"
              minWidth="70px"
              offset={toggleSidebar ? "15px" : "25px"}
            >
              <button
                onClick={toggleSidebarIcon}
                className={`v-nav-hover p-2 rounded ${
                  toggleSidebar
                    ? "text-white"
                    : "fill-black relative left-6 hover:bg-slate-gray-200"
                }`}
              >
                {toggleSidebar ? <IconCollapse /> : <IconExpand />}
              </button>
            </Tooltip>
          </div>

          <div
            className="overflow-y-auto flex-1 overflow-x-hidden"
            style={{
              maxHeight: "calc(100vh - 4rem)",
              scrollbarColor: "#d1e4ff #3794ff",
              scrollbarWidth: "thin"
            }}
          >
            {menuItems.map(renderMenu)}
          </div>

          <div className="border-t v-nav-sep my-2">
            <div className="py-2">
              <MainLink
                to={messagingClientUrl}
                label="Agent Workspace"
                icon={<LinkExternal01 />}
                onVisit={() => {}}
                showTooltip={toggleSidebar}
                toggleSidebar={toggleSidebar}
                target="_blank"
              />
            </div>
            {/* Inline font-size control — visible at the bottom of the expanded sidebar */}
            {toggleSidebar && (
              <div className="flex items-center gap-1.5 px-3 pb-2">
                <span className="text-xs v-nav-text opacity-60 flex-1 select-none">Text size</span>
                <button
                  type="button"
                  onClick={() => changeFontSize(-5)}
                  disabled={contentFontPct <= 80}
                  className="w-7 h-7 rounded-md v-nav-hover flex items-center justify-center disabled:opacity-30"
                  aria-label="Decrease text size"
                  title="Smaller text"
                ><i className="fa-solid fa-minus text-xs" aria-hidden="true" /></button>
                <span className="text-xs v-nav-text opacity-60 select-none w-8 text-center">{contentFontPct}%</span>
                <button
                  type="button"
                  onClick={() => changeFontSize(5)}
                  disabled={contentFontPct >= 130}
                  className="w-7 h-7 rounded-md v-nav-hover flex items-center justify-center disabled:opacity-30"
                  aria-label="Increase text size"
                  title="Larger text"
                ><i className="fa-solid fa-plus text-xs" aria-hidden="true" /></button>
              </div>
            )}
            <div className="border-t v-nav-sep"></div>
            <div className="relative flex items-center">
              <div className="flex-1">
                <ProfileButton
                  picture={profile.picture}
                  availabilityColor={availabilityColor}
                  onToggle={() => {
                    setShowProfileMenu((prev) => !prev);
                  }}
                  label={
                    profile.firstName && profile.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile.displayName
                  }
                  toggleSidebar={toggleSidebar}
                />
              </div>
            </div>
            {showProfileMenu && (
              <DesktopProfileMenu
                onClose={() => setShowProfileMenu(false)}
                profile={profile}
                contentFontPct={contentFontPct}
                onFontIncrease={() => changeFontSize(5)}
                onFontDecrease={() => changeFontSize(-5)}
              />
            )}
            <div className="border-t v-nav-sep"></div>
          </div>
        </div>

        <main
          className={`flex-auto min-w-0 bg-slate-gray-50 dark:bg-slate-gray-900 dark:text-slate-gray-100 shrink overflow-auto h-full ${
            toggleSidebar ? "p-3 sm:p-6" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16"
          }`}
          style={contentFontPct !== 100 ? { zoom: `${contentFontPct}%` } : undefined}
        >
          {/* Font size controls — sticky top-right, always visible */}
          <div className="sticky top-0 z-10 flex justify-end pointer-events-none -mt-1 mb-2">
            <div className="flex items-center gap-0.5 bg-white dark:bg-slate-gray-800 border border-slate-gray-200 dark:border-slate-gray-700 rounded-xl px-1 py-0.5 shadow-md pointer-events-auto">
              <button
                type="button"
                onClick={() => changeFontSize(-5)}
                disabled={contentFontPct <= 80}
                className="w-8 h-8 rounded-lg hover:bg-slate-gray-100 dark:hover:bg-slate-gray-700 active:bg-slate-gray-200 dark:active:bg-slate-gray-600 flex items-center justify-center disabled:opacity-30 text-sm font-bold text-slate-gray-600 dark:text-slate-gray-200 transition-colors"
                aria-label="Decrease text size"
                title="Smaller text"
              >
                <i className="fa-solid fa-minus text-xs" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => { changeFontSize(100 - contentFontPct); }}
                className="text-xs font-semibold text-slate-gray-500 dark:text-slate-gray-300 select-none px-1.5 min-w-[40px] text-center hover:text-cornflower-blue-600 dark:hover:text-cornflower-blue-400 transition-colors rounded-md hover:bg-slate-gray-50 dark:hover:bg-slate-gray-700 py-1"
                title="Reset to 100%"
              >{contentFontPct}%</button>
              <button
                type="button"
                onClick={() => changeFontSize(5)}
                disabled={contentFontPct >= 130}
                className="w-8 h-8 rounded-lg hover:bg-slate-gray-100 dark:hover:bg-slate-gray-700 active:bg-slate-gray-200 dark:active:bg-slate-gray-600 flex items-center justify-center disabled:opacity-30 text-sm font-bold text-slate-gray-600 dark:text-slate-gray-200 transition-colors"
                aria-label="Increase text size"
                title="Larger text"
              >
                <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Update notification — shown when the server version changes */}
          {showReloadModal && (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 px-4 py-3 bg-cornflower-blue-600 text-white rounded-lg shadow">
              <span className="text-sm font-medium">
                A new version of the app is available.
              </span>
              <div className="flex items-center gap-3 ml-4">
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm font-semibold bg-white text-cornflower-blue-600 px-3 py-1 rounded hover:bg-cornflower-blue-50 transition-colors"
                >
                  Reload now
                </button>
                <button
                  onClick={resetModal}
                  className="text-sm text-cornflower-blue-100 hover:text-white transition-colors"
                  aria-label="Dismiss update notification"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          {props.children}
        </main>
      </div>

      {/* Moshky — global FAB, always available on every page */}
      {!showCopilot && (
        <button
          type="button"
          onMouseDown={(e) => {
            fabDragging.current = false;
            fabDragStart.current = { mx: e.clientX, my: e.clientY, ob: fabPos.bottom, or: fabPos.right };
            e.preventDefault();
          }}
          onClick={() => { if (!fabDragging.current) setShowCopilot(true); }}
          style={{ bottom: fabPos.bottom, right: fabPos.right }}
          className="fixed z-[9999] flex items-center gap-2.5 bg-cornflower-blue-600 hover:bg-cornflower-blue-700 text-white rounded-xl px-5 py-3.5 shadow-xl hover:shadow-2xl transition-all text-base font-semibold cursor-grab active:cursor-grabbing select-none hover:scale-105"
          aria-label="Open Moshky"
        >
          <svg width="24" height="20" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z" fill="white"/>
            <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
            <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
          </svg>
          Ask Moshky
        </button>
      )}
      {showCopilot && (
        <CopilotAssistant
          context={`Current page: ${location.pathname}. Use [→ Label](/path) format when directing the user to any page.`}
          integrationKey={integrationKey}
          onClose={() => setShowCopilot(false)}
        />
      )}
    </div>
  );
}
